/**
 * BGM 素材の表示名 (シナリオ編集画面の BGM ヘッダー / 履歴セレクタのトリガーに出す「曲名」)。
 *
 * 優先順は backend の音声素材の表示名規約 (routes/brands/sound_logos.py
 * `_sound_logo_display_name`) に揃える:
 *   1. name (ユーザーが付けた識別名。リネームしても曲名表示が変わらないと識別できない)
 *   2. generation_metadata.song_title (ElevenLabs / プロンプト上書き時の曲名。Lyria は空)
 *   3. generation_metadata.filename (アップロード時の元ファイル名)
 *   4. 自動生成 BGM (provider / prompt を持つ) は「BGM (自動生成)」
 *   5. storage_path のファイル名から、アップロード時に付くタイムスタンプ接頭辞と拡張子を除いたもの
 *
 * 従来はシナリオ読み込み時だけ 5 を直接使っていたため、Lyria で生成した BGM
 * (name も song_title も空) が「20260909150312123456_bgm_商品名_20260909_150312123456」
 * のような文字列で表示され、生成直後の「BGM (自動生成)」とリロード後で表示が食い違っていた。
 */
export type BgmDisplayNameSource = {
  name?: string | null;
  source_type?: string | null;
  generation_metadata?: Record<string, unknown> | null;
  storage_path?: string | null;
  temp_path?: string | null;
};

export const GENERATED_BGM_LABEL = 'BGM (自動生成)';

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** storage_path / temp_path の末尾ファイル名から、アップロード時のタイムスタンプ接頭辞と拡張子を除く */
export function bgmFileNameFromPath(path: string | null | undefined): string {
  const last = (path || '').split('/').pop() || '';
  return last
    .replace(/\.[^.]+$/, '')
    .replace(/^\d{14,}_/, '');
}

export function resolveBgmDisplayName(material: BgmDisplayNameSource | null | undefined): string | null {
  if (!material) return null;
  const name = str(material.name);
  if (name) return name;
  const meta = (material.generation_metadata && typeof material.generation_metadata === 'object')
    ? material.generation_metadata
    : {};
  const songTitle = str(meta.song_title);
  if (songTitle) return songTitle;
  const filename = str(meta.filename);
  if (filename) return filename;
  const isGenerated = material.source_type === 'bgm' && (str(meta.provider) !== '' || str(meta.prompt) !== '');
  if (isGenerated) return GENERATED_BGM_LABEL;
  const fromPath = bgmFileNameFromPath(material.temp_path || material.storage_path);
  return fromPath || null;
}
